import React, { useMemo, useState } from 'react';
import { TARIFA } from '../data';
import type { AppState, Consumo, Sucursal } from '../types';
import { PageShell, StatCard, T, glassLight, inputStyle, primaryButton, ghostButton, useIsMobile, usePagination, Pagination, Modal } from '../ui';
const money = (n: number) => 'RD$ ' + Math.round(n).toLocaleString('es-DO');
const kwhFmt = (n: number) => Math.round(n).toLocaleString('es-DO') + ' kWh';
const monthLabel = (ts: number) => new Date(ts).toLocaleDateString('es-DO', {
  month: 'short',
  year: '2-digit'
});
// Fecha + hora en horario de República Dominicana (America/Santo_Domingo).
const drDateTime = (ts: number) => new Date(ts).toLocaleString('es-DO', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  timeZone: 'America/Santo_Domingo'
});
// Consumo de cada lectura = lectura actual − lectura anterior (cronológico).
function buildSeries(consumos: Consumo[]) {
  const asc = [...consumos].sort((a, b) => a.fecha - b.fecha);
  return asc.map((c, i) => ({
    ...c,
    consumo: i === 0 ? 0 : Math.max(0, c.kwh - asc[i - 1].kwh)
  }));
}
interface Props {
  state: AppState;
  sucursal: Sucursal;
  sucursalesDisponibles: Sucursal[];
  puntoFijo: boolean;
  mostrarCosto?: boolean;
  onChangeSucursal: (id: string) => void;
  onSave: (sucursalId: string, fecha: number, kwh: number) => void;
  onDelete?: (id: string) => void;
  canArchive?: boolean;
}
export function ConsumoEnergetico({
  state,
  sucursal,
  sucursalesDisponibles,
  puntoFijo,
  mostrarCosto = true,
  onChangeSucursal,
  onSave,
  onDelete,
  canArchive
}: Props) {
  const [tab, setTab] = useState<'activos' | 'archivados'>('activos');
  const [confirm, setConfirm] = useState<string | null>(null);
  const isMobile = useIsMobile();
  const today = new Date();
  const [fechaStr, setFechaStr] = useState(today.toISOString().slice(0, 10));
  const [lectura, setLectura] = useState('');
  const series = useMemo(() => buildSeries(state.consumos.filter(c => c.sucursalId === sucursal.id)), [state.consumos, sucursal.id]);
  const historial = useMemo(() => [...series].reverse().filter(c => tab === 'activos' ? !c.archivado : c.archivado), [series, tab]);
  const {
    page,
    setPage,
    totalPages,
    pageItems: histItems,
    rangeStart,
    rangeEnd,
    total
  } = usePagination(historial, 8);
  const ultima = series[series.length - 1];
  const consumoActual = ultima?.consumo ?? 0;
  const now = new Date();
  const inMonth = (ts: number, offset: number) => {
    const d = new Date(ts);
    const ref = new Date(now.getFullYear(), now.getMonth() - offset, 1);
    return d.getFullYear() === ref.getFullYear() && d.getMonth() === ref.getMonth();
  };
  const consumoMes = series.filter(c => inMonth(c.fecha, 0)).reduce((a, c) => a + c.consumo, 0);
  const consumoMesAnterior = series.filter(c => inMonth(c.fecha, 1)).reduce((a, c) => a + c.consumo, 0);
  const costoMes = consumoMes * TARIFA;
  const variacion = consumoMesAnterior > 0 ? Math.round((consumoMes - consumoMesAnterior) / consumoMesAnterior * 100) : null;
  // Últimos 6 meses para el gráfico
  const chartData = series.filter(c => c.consumo > 0).slice(-6);
  const maxConsumo = Math.max(1, ...chartData.map(c => c.consumo));
  const ultimaLectura = ultima?.kwh;
  const guardar = () => {
    const kwh = parseFloat(lectura);
    if (Number.isNaN(kwh) || kwh < 0) {
      alert('Ingresa la lectura del medidor en kWh.');
      return;
    }
    // El medidor es acumulativo: una lectura menor que la anterior es inválida
    // y no se permite registrar.
    if (ultimaLectura !== undefined && kwh < ultimaLectura) {
      alert(`La lectura (${kwhFmt(kwh)}) no puede ser menor que la anterior (${kwhFmt(ultimaLectura)}). El medidor es acumulativo, ingresa un valor igual o mayor.`);
      return;
    }
    // Guardamos la fecha elegida con la HORA exacta del registro (zona RD).
    const ahora = new Date();
    const hh = String(ahora.getHours()).padStart(2, '0');
    const mm = String(ahora.getMinutes()).padStart(2, '0');
    const ss = String(ahora.getSeconds()).padStart(2, '0');
    const fecha = new Date(`${fechaStr}T${hh}:${mm}:${ss}`).getTime();
    onSave(sucursal.id, fecha, kwh);
    setLectura('');
  };
  return <PageShell title="Control de Consumo Energético" sub="Registra la lectura de tu medidor. El sistema calcula el resto." data-id="element-344">
      <style data-id="element-345">{`
        .ce-bar { transition: height .4s cubic-bezier(.2,.8,.2,1); }
        .ce-save:hover { opacity:.9; transform: translateY(-1px); }
        .ce-save { transition: all .15s; }
      `}</style>

      {/* ── Tarjeta principal de registro ── */}
      <div style={{
      background: 'linear-gradient(135deg, rgba(255,107,1,.08), rgba(255,107,1,.02))',
      border: `1px solid rgba(255,107,1,.25)`,
      borderRadius: 20,
      padding: isMobile ? 18 : 26
    }} data-id="element-346">
        <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 16,
        flexWrap: 'wrap',
        marginBottom: 22
      }} data-id="element-347">
          <div data-id="element-348">
            <div style={{
            fontSize: 11,
            fontWeight: 700,
            color: T.accent,
            letterSpacing: '.1em',
            textTransform: 'uppercase',
            marginBottom: 6
          }} data-id="element-349">
              Punto Comercial
            </div>
            {puntoFijo ? <div style={{
            fontSize: 22,
            fontWeight: 800,
            color: T.text,
            letterSpacing: '-0.02em'
          }} data-id="element-350">
                {sucursal.nombre}
              </div> : <select value={sucursal.id} onChange={e => onChangeSucursal(e.target.value)} style={{
            ...inputStyle,
            width: isMobile ? '100%' : 'auto',
            minWidth: isMobile ? 0 : 220,
            fontSize: isMobile ? 16 : 18,
            fontWeight: 700,
            padding: '8px 12px'
          }} data-id="element-351">
                {sucursalesDisponibles.map(s => <option key={s.id} value={s.id} data-id="element-352">
                    {s.nombre}
                  </option>)}
              </select>}
            <div style={{
            fontSize: 12,
            color: T.textSecondary,
            marginTop: 5
          }} data-id="element-353">
              {sucursal.direccion}
            </div>
          </div>
          {ultimaLectura !== undefined && <div style={{
          textAlign: 'right'
        }} data-id="element-354">
              <div style={{
            fontSize: 11,
            color: T.textMuted,
            fontWeight: 600
          }} data-id="element-355">
                Última lectura
              </div>
              <div style={{
            fontSize: 16,
            fontWeight: 700,
            color: T.text,
            marginTop: 2
          }} data-id="element-356">
                {kwhFmt(ultimaLectura)}
              </div>
            </div>}
        </div>

        <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr auto',
        gap: 14,
        alignItems: 'end',
        maxWidth: 720
      }} data-id="element-357">
          <div data-id="element-358">
            <label style={{
            display: 'block',
            fontSize: 11,
            fontWeight: 700,
            color: T.textMuted,
            letterSpacing: '.08em',
            textTransform: 'uppercase',
            marginBottom: 7
          }} data-id="element-359">
              Fecha
            </label>
            <input type="date" value={fechaStr} onChange={e => setFechaStr(e.target.value)} style={inputStyle} data-id="element-360" />
          </div>
          <div data-id="element-361">
            <label style={{
            display: 'block',
            fontSize: 11,
            fontWeight: 700,
            color: T.textMuted,
            letterSpacing: '.08em',
            textTransform: 'uppercase',
            marginBottom: 7
          }} data-id="element-362">
              Lectura del Medidor (kWh)
            </label>
            <input type="number" min="0" inputMode="decimal" value={lectura} onChange={e => setLectura(e.target.value)} placeholder="Ej: 290500" style={inputStyle} onKeyDown={e => {
            if (e.key === 'Enter') guardar();
          }} data-id="element-363" />
          </div>
          <button className="ce-save" onClick={guardar} style={{
          ...primaryButton,
          padding: '12px 22px',
          fontSize: 14,
          height: 44,
          width: isMobile ? '100%' : 'auto'
        }} data-id="element-364">
            Guardar Consumo
          </button>
        </div>
        <div style={{
        fontSize: 12,
        color: T.textMuted,
        marginTop: 12
      }} data-id="element-365">
          Solo escribe el número que muestra el medidor. El sistema calcula el
          consumo y el costo automáticamente.
        </div>
      </div>

      {/* ── Resultados ── */}
      <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit,minmax(190px,1fr))',
      gap: 12
    }} data-id="element-366">
        <StatCard label="Consumo actual" value={kwhFmt(consumoActual)} note="Última lectura registrada" accent={T.info} data-id="element-367" />
        <StatCard label="Consumo del mes" value={kwhFmt(consumoMes)} note={variacion === null ? 'Mes en curso' : `${variacion >= 0 ? '▲' : '▼'} ${Math.abs(variacion)}% vs mes anterior`} accent={variacion !== null && variacion > 0 ? T.warn : T.ok} data-id="element-368" />
        {mostrarCosto && <StatCard label="Costo estimado" value={money(costoMes)} note={`Tarifa interna ${TARIFA} RD$/kWh`} accent="#c4b5fd" data-id="element-369" />}
      </div>

      {/* ── Gráfico de consumo mensual ── */}
      <div style={{
      fontSize: 11,
      fontWeight: 700,
      color: T.textMuted,
      letterSpacing: '.1em',
      textTransform: 'uppercase',
      marginTop: 8,
      marginBottom: 4
    }} data-id="element-370">
        Consumo mensual
      </div>
      <div style={{
      ...glassLight,
      padding: isMobile ? 16 : 24
    }} data-id="element-371">
        {chartData.length === 0 ? <div style={{
        textAlign: 'center',
        color: T.textMuted,
        fontSize: 13,
        padding: '24px 0'
      }} data-id="element-372">
            Aún no hay suficientes lecturas para graficar.
          </div> : <div style={{
        display: 'flex',
        alignItems: 'flex-end',
        gap: isMobile ? 8 : 18,
        height: isMobile ? 160 : 200,
        padding: '0 4px'
      }} data-id="element-373">
            {chartData.map(c => {
          const h = Math.round(c.consumo / maxConsumo * 160) + 4;
          const cost = c.consumo * TARIFA;
          return <div key={c.id} style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 8
          }} data-id="element-374">
                  <div style={{
              fontSize: 12,
              fontWeight: 700,
              color: T.text
            }} data-id="element-375">
                    {Math.round(c.consumo / 1000)}k
                  </div>
                  <div className="ce-bar" title={`${kwhFmt(c.consumo)} · ${money(cost)}`} style={{
              width: '100%',
              maxWidth: 54,
              height: h,
              borderRadius: '8px 8px 0 0',
              background: 'linear-gradient(180deg,#FF8C00,#FF5A00)',
              boxShadow: '0 0 18px rgba(255,107,1,.35)'
            }} data-id="element-376" />
                  <div style={{
              fontSize: 11,
              color: T.textMuted,
              fontWeight: 600
            }} data-id="element-377">
                    {monthLabel(c.fecha)}
                  </div>
                </div>;
        })}
          </div>}
      </div>

      {/* ── Comparación ── */}
      <div style={{
      ...glassLight,
      padding: 20,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: 16,
      flexWrap: 'wrap'
    }} data-id="element-378">
        <div data-id="element-379">
          <div style={{
          fontSize: 13,
          fontWeight: 700,
          color: T.text
        }} data-id="element-380">
            Comparación con el mes anterior
          </div>
          <div style={{
          fontSize: 12,
          color: T.textSecondary,
          marginTop: 3
        }} data-id="element-381">
            Mes actual {kwhFmt(consumoMes)} · Mes anterior{' '}
            {kwhFmt(consumoMesAnterior)}
          </div>
        </div>
        <div style={{
        background: variacion !== null && variacion > 0 ? T.warnSoft : T.okSoft,
        color: variacion !== null && variacion > 0 ? T.warn : T.ok,
        borderRadius: 99,
        padding: '8px 16px',
        fontWeight: 700,
        fontSize: 14
      }} data-id="element-382">
          {variacion === null ? 'Sin referencia' : `${variacion >= 0 ? '+' : ''}${variacion}%`}
        </div>
      </div>

      {/* ── Historial ── */}
      <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginTop: 8,
      marginBottom: 4
    }} data-id="element-383">
        <div style={{
        fontSize: 11,
        fontWeight: 700,
        color: T.textMuted,
        letterSpacing: '.1em',
        textTransform: 'uppercase'
      }} data-id="element-384">
          Historial de registros
        </div>
        <div style={{
        display: 'flex',
        gap: 8
      }} data-id="element-385">
          <button onClick={() => {
          setTab('activos');
          setPage(1);
        }} style={{
          ...ghostButton,
          padding: '4px 8px',
          fontSize: 11,
          background: tab === 'activos' ? T.accentSoft : 'transparent',
          color: tab === 'activos' ? T.accent : T.textMuted
        }} data-id="element-386">
            Activos
          </button>
          {canArchive && <button onClick={() => {
          setTab('archivados');
          setPage(1);
        }} style={{
          ...ghostButton,
          padding: '4px 8px',
          fontSize: 11,
          background: tab === 'archivados' ? T.accentSoft : 'transparent',
          color: tab === 'archivados' ? T.accent : T.textMuted
        }} data-id="element-387">
              Archivados
            </button>}
        </div>
      </div>
      <div style={{
      ...glassLight,
      overflow: 'hidden'
    }} data-id="element-388">
        <div style={{
        overflowX: 'auto'
      }} data-id="element-389">
          <div style={{
          display: 'grid',
          gridTemplateColumns: mostrarCosto ? onDelete ? '1.2fr 1fr 1fr 1fr auto' : '1.2fr 1fr 1fr 1fr' : onDelete ? '1.2fr 1fr 1fr auto' : '1.2fr 1fr 1fr',
          minWidth: isMobile ? 420 : undefined,
          background: T.surfaceSubtle,
          borderBottom: `1px solid ${T.border}`
        }} data-id="element-390">
            {(mostrarCosto ? onDelete ? ['Fecha', 'Lectura', 'Consumo', 'Costo', ''] : ['Fecha', 'Lectura', 'Consumo', 'Costo'] : onDelete ? ['Fecha', 'Lectura', 'Consumo', ''] : ['Fecha', 'Lectura', 'Consumo']).map((h, i) => <div key={h} style={{
            padding: '12px 16px',
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '.08em',
            textTransform: 'uppercase',
            color: T.textMuted,
            textAlign: i === 0 ? 'left' : 'right'
          }} data-id="element-391">
                {h}
              </div>)}
          </div>
          {series.length === 0 && <div style={{
          padding: '28px 16px',
          textAlign: 'center',
          color: T.textMuted,
          fontSize: 13
        }} data-id="element-392">
              Sin registros aún
            </div>}
          {histItems.map((c, i, arr) => <div key={c.id} style={{
          display: 'grid',
          gridTemplateColumns: mostrarCosto ? onDelete ? '1.2fr 1fr 1fr 1fr auto' : '1.2fr 1fr 1fr 1fr' : onDelete ? '1.2fr 1fr 1fr auto' : '1.2fr 1fr 1fr',
          minWidth: isMobile ? 420 : undefined,
          borderBottom: i < arr.length - 1 ? `1px solid ${T.border}` : 'none',
          alignItems: 'center'
        }} data-id="element-393">
              <div style={{
            padding: '13px 16px',
            fontSize: 13,
            color: T.text,
            fontWeight: 600
          }} data-id="element-394">
                {drDateTime(c.fecha)}
              </div>
              <div style={{
            padding: '13px 16px',
            fontSize: 13,
            color: T.textSecondary,
            textAlign: 'right'
          }} data-id="element-395">
                {kwhFmt(c.kwh)}
              </div>
              <div style={{
            padding: '13px 16px',
            fontSize: 13,
            color: T.text,
            fontWeight: 600,
            textAlign: 'right'
          }} data-id="element-396">
                {c.consumo > 0 ? kwhFmt(c.consumo) : '—'}
              </div>
              {mostrarCosto && <div style={{
            padding: '13px 16px',
            fontSize: 13,
            color: T.accent,
            fontWeight: 600,
            textAlign: 'right'
          }} data-id="element-397">
                  {c.consumo > 0 ? money(c.consumo * TARIFA) : '—'}
                </div>}
              {onDelete && <div style={{
            padding: '8px 16px',
            textAlign: 'right'
          }} data-id="element-398">
                  <button onClick={() => setConfirm(c.id)} style={{
              ...ghostButton,
              padding: '4px 8px',
              fontSize: 11,
              color: c.archivado ? T.info : T.warn,
              borderColor: c.archivado ? 'rgba(96,165,250,0.3)' : 'rgba(251,191,36,0.3)',
              background: c.archivado ? 'rgba(96,165,250,0.1)' : 'rgba(251,191,36,0.1)'
            }} data-id="element-399">
                    {c.archivado ? 'Desarchivar' : 'Archivar'}
                  </button>
                </div>}
            </div>)}
          {series.length > 0 && <div style={{
          padding: '4px 16px 14px'
        }} data-id="element-400">
              <Pagination page={page} totalPages={totalPages} onChange={setPage} rangeStart={rangeStart} rangeEnd={rangeEnd} total={total} label="registros" data-id="element-401" />
            </div>}
        </div>
      </div>

      {confirm && <Modal title="Confirmar acción" onClose={() => setConfirm(null)} data-id="element-402">
          <div style={{
        color: T.textSecondary,
        marginBottom: 24,
        fontSize: 14,
        lineHeight: 1.5
      }} data-id="element-403">
            ¿Estás seguro de que deseas{' '}
            {state.consumos.find(c => c.id === confirm)?.archivado ? 'desarchivar' : 'archivar'}{' '}
            este registro de consumo?
          </div>
          <div style={{
        display: 'flex',
        gap: 12,
        justifyContent: 'flex-end'
      }} data-id="element-404">
            <button onClick={() => setConfirm(null)} style={{
          ...ghostButton,
          padding: '10px 20px'
        }} data-id="element-405">
              Cancelar
            </button>
            <button onClick={() => {
          if (onDelete) onDelete(confirm);
          setConfirm(null);
        }} style={{
          ...primaryButton,
          background: state.consumos.find(c => c.id === confirm)?.archivado ? T.info : T.warn,
          borderColor: state.consumos.find(c => c.id === confirm)?.archivado ? T.info : T.warn,
          color: '#0B1120'
        }} data-id="element-406">
              Sí,{' '}
              {state.consumos.find(c => c.id === confirm)?.archivado ? 'desarchivar' : 'archivar'}
            </button>
          </div>
        </Modal>}
    </PageShell>;
}